import type { Messages } from '../messages.js'

/**
 * Irish. `beart` for flips, the word an Irish board game uses for a move.
 *
 * Five plural forms, and they are not five ways of saying the same thing: an Irish numeral
 * mutates the noun after it, and which mutation depends on the number. One to six lenite —
 * `trí fhocal` — seven to ten eclipse — `seacht bhfocal` — and from eleven the noun is bare
 * again. The plural forms here carry those mutations, which is why the same word is spelt three
 * ways below.
 */
export const ga: Messages = {
  tag: 'ga',

  readingDictionary: 'Ag léamh an fhoclóra…',
  noWordList: 'Níl liosta focal ann do "{language}". Cruthaigh ceann:  pnpm dictionary build',
  emptyWordList: 'Tá liosta focal "{language}" folamh.',

  flips: 'bearta',
  score: 'scór',
  words: 'focail',
  round: 'babhta',
  ticksLeftLabel: 'An t-am atá fágtha sa bhabhta seo',
  typeAWord: 'clóscríobh focal',
  tapPrompt: 'tapáil litreacha chun iad a roghnú nó a thabhairt ar ais, ansin {action}',

  boardOfTiles: 'Clár de {n} tíl',
  faceDown: 'béal faoi',
  wildCard: 'cárta fiáin',
  wildKey: 'litir ar bith',
  letterReplaced: 'rinneadh {to} de {from}',
  letterSwap: 'MALARTÚ LITREACHA!',
  spentTile: 'tíl caite',
  hiddenWhilePaused: 'i bhfolach le linn sosa',
  letterInWord: '{letter}, litir {position} den fhocal',

  completeWord: 'Críochnaigh an focal',

  completeShort: 'Críochnaigh',
  reset: 'Glan',
  pause: 'Sos',
  resume: 'Lean ar aghaidh',
  newGame: 'Cluiche nua',
  paused: 'Ar sos',
  outOfFlips: 'Bearta ídithe',
  finalResult: '{score} as {words} thar {rounds}',
  playAgain: 'Imir arís',
  share: 'Comhroinn',
  shareCopied: 'Cóipeáilte.',
  shareSelect: 'Cóipeáil é seo:',

  lettersSelect: 'roghnaíonn litreacha',
  keysWild: 'tógtar é nuair a chlóscríobhann tú litir nach bhfuil ar aon tíl',
  clearsEvery: 'glanann gach {letter} roghnaithe',
  undoLastLetter: 'cealaigh an litir dheireanach',
  noWordsYet: 'Gan focal ar bith fós.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'aimsithe cheana',
  reasonTooShort: 'róghearr',
  reasonNotAWord: 'ní focal é',
  reasonAllFound: 'tá siad go léir agat cheana',
  noSuchLetterUp: 'níl {letter} ar bith iompaithe',
  nothingUp: 'níl aon rud iompaithe',
  letterHidden: 'chuaigh litir i bhfolach',
  shuffled: 'suaite',
  shuffledAndBilled: 'suaite, gearradh {flips} gan úsáid',
  tooFewLetters: 'Ró-bheagán litreacha fágtha',

  gameLanguage: 'teanga',
  interfaceLanguage: 'comhéadan',
  dictionarySize: '{common} coitianta as {full} focal',
  filterLanguages: 'Cuardaigh teanga',
  noMatches: 'Gan torthaí',

  nerdMode: 'mód na ndíograiseoirí',
  rules: 'Rialacha',
  difficulty: 'deacracht',
  difficultyNames: { easy: 'éasca', medium: 'measartha', hard: 'deacair', insane: 'mire' },
  pausedNoBoard: 'Ní bheidh an cluiche seo ar an gclár ceannais.',
  about: 'Maidir leis',
  themeLabel: 'téama',
  themeNames: { traditional: 'traidisiúnta', light: 'sorcha', contrast: 'codarsnacht ard' },
  tiles: 'tíleanna (N)',
  secondsPerTick: 'soicindí / tic',
  holdTicks: 'ticeanna coinneála',
  minWord: 'an focal is giorra',
  startingFlips: 'bearta tosaigh',
  wildChance: 'seans cárta fiáin',
  replaceChance: 'seans malartaithe litreach',
  hideChance: 'seans litir a chur i bhfolach',
  wordCompleteMode: 'nuair a chríochnaítear focal',
  wordCompleteNames: { shuffle: 'suaith', spend: 'caith', keep: 'coinnigh' },
  flipEconomy: 'aisíoc na mbeart',
  flipEconomyNames: {
    none: 'gan aon cheann',
    perLetter: 'de réir litreach',
    fibonacci: 'fibonacci',
    overMinimum: 'os cionn an íosmhéid',
  },
  repeatedLetterKey: 'eochair na litreach athdhéanta',
  keySchemeNames: { cycle: 'timthriall', advance: 'ar aghaidh' },
  keySchemeHelp: {
    cycle:
      'Tógann A an chéad A eile nach bhfuil in úsáid, agus nuair atá siad go léir san fhocal, ' +
      'glanann sé iad. Glanann Shift+A iad freisin.',
    advance: 'Tógann A an chéad A eile nach bhfuil in úsáid. Glanann Shift+A gach A san fhocal.',
  },

  whatThatMeans: 'Cad is brí leis sin',
  factRound: 'babhta',
  factWholeBoardUp: 'an clár ar fad iompaithe ar feadh',
  factRoundCosts: 'cosnaíonn babhta',
  factFlipsBuy: 'ceannaíonn na bearta tosaigh',
  factThisBoard: 'an clár seo',
  factBoardHadToAdmit: 'bhí ar an gclár glacadh le',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, an ceann is faide {longest}',
  wordsIncludingOneOf: '{words}, ceann acu {ceiling}',
  scorelessRounds: '{rounds} gan scór',

  whatAWordPays: 'An méid a íocann focal',
  columnLetters: 'litreacha',
  columnCost: 'costas',
  columnPoints: 'pointí',
  columnFlips: 'bearta',
  columnNet: 'glan',

  canonicalRules: 'Gnáthrialacha {difficulty}.',
  customRules: 'Athraithe ón réamhshocrú. Ní chuirfear scóir faoi rialacha féin ar an liosta.',
  applyAndStart: 'Cuir i bhfeidhm agus tosaigh cluiche nua',
  changesNextGame: 'Tiocfaidh na hathruithe i bhfeidhm sa chéad chluiche eile.',
  presets: 'Réamhshocruithe:',

  start: 'Tosaigh',
  restart: 'Atosaigh',
  quit: 'Éirigh as',
  quitTitle: 'Éirí as an gcluiche seo?',
  restartTitle: 'An cluiche seo a atosú?',
  restartConfirm: 'Atosaigh',
  quitConfirm: 'Éirigh as',
  keepPlaying: 'Lean ort ag imirt',
  personalBest: 'Na cluichí is fearr agat',
  newPersonalBest: 'Curiarracht phearsanta nua.',
  columnRank: '#',
  notRanked: 'Rialacha féin, mar sin níl an cluiche seo ar an liosta.',
  notRankedPaused: 'Stop an clog, mar sin níl an cluiche seo ar an liosta.',
  rankOfTotal: '{rank} as {total}',

  howToPlay: 'Conas imirt',

  backToGame: 'Ar ais chuig an gcluiche',
  welcomeTitle: 'Fáilte go Blinkered',
  tutorialSkip: 'Scipeáil',
  tutorialNext: 'Ar aghaidh',
  tutorialBack: 'Siar',
  tutorialStart: 'Tosaigh ag imirt',
  tutorialHideAgain: 'Ná taispeáin é seo arís',
  tutorialProgress: '{n} as {total}',
  tutorialSkipTitle: 'An turas a scipeáil?',
  tutGoalTitle: 'Rás is ea Blinkered',
  tutGoalBody:
    'Tosaíonn tú an cluiche le líon socraithe beart, agus cosnaíonn gach tíl a iompaítear beart amháin. Nuair a ídítear na bearta, tá an cluiche thart. Tuilleann focail bearta, agus dá fhaide an focal, is ea is mó beart a thuilleann tú.',
  tutSpentBody:
    'Casann na litreacha a úsáideann tú i bhfocal ar a n-aghaidh go dtí an chéad bhabhta eile. Ní athraítear na litreacha ar an gclár ach nuair a chuirtear litir eile in ionad litreach.',
  tutChangeTitle: 'Is féidir le litreacha athrú',
  tutSameLetters:
    'An bhfaca tú focal nach raibh deis agat é a imirt? Tagann na litreacha céanna ar ais sa chéad bhabhta eile, suaite in áiteanna nua.',
  tutPickLetters: 'Tapáil na litreacha atá uait, in ord, chun focal a dhéanamh.',
  tutMoreTurn:
    'Leanann na tíleanna ag iompú fad is atá tú ag machnamh, mar sin d’fhéadfadh litir níos fearr a bheith ar an mbealach.',
  tutTapBack:
    'Ar thóg tú ceann nach raibh uait? Tapáil arís í chun í a thabhairt ar ais. Ceann ar bith, ní hí an ceann deireanach amháin.',
  tutComplete: 'Brúigh Críochnaigh nuair atá an focal réidh.',
  htBoardTitle: 'An clár',
  htBoardBody:
    'Iompaítear na tíleanna ceann ar cheann, in ord randamach. Is féidir focail a dhéanamh as na cinn atá iompaithe.',
  htWordsTitle: 'Na focail',
  htWordsBody:
    'Déan focal as na tíleanna atá le feiceáil trí na litreacha a chlóscríobh nó a chliceáil in ord.',
  htFlipsTitle: 'Na bearta',
  htFlipsBody:
    'Cosnaíonn gach tíl a iompaítear beart. Tugann focal críochnaithe bearta ar ais, agus íocann focail níos faide níos mó. Nuair a ídítear na bearta, tá an cluiche thart.',
  htRoundTitle: 'An babhta',
  htRoundBody:
    'Nuair a iompaítear tíl dheireanach an bhabhta, fanann an clár ar fad ar feadh soicind. Ansin iompaítear na tíleanna agus suaitear iad, agus tosaíonn babhta nua.',
  htLanguagesTitle: 'Na teangacha',
  htLanguagesBody:
    'Tá {n} acu ann. Is féidir gach clár a réiteach le focail a úsáideann daoine i ndáiríre. Faigheann focal neamhchoitianta scór freisin, má tá aithne ag an bhfoclóir air.',
  htKeysTitle: 'An méarchlár',
  htWildTitle: 'Cártaí fiáine',
  htWildBody:
    'Uaireanta tagann cárta fiáin in ionad litreach. Comhaireamh cárta fiáin mar litir ar bith a dhéanann focal bailí. Ní chomhairtear focal atá críochnaithe cheana.',
  htHideTitle: 'Litreacha a théann i bhfolach',
  htHideBody:
    'Ar na leibhéil is deacra féadann litir atá le feiceáil casadh ar ais uaithi féin agus teacht ar ais níos déanaí sa bhabhta. Faigheann an t-amadóir tic breise dá bharr, i ndearg. Ní chasann litreacha atá roghnaithe agat ar ais.',
  tutHideBody:
    'Ar na leibhéil is deacra féadann litir atá le feiceáil casadh ar ais uaithi féin agus teacht ar ais níos déanaí sa bhabhta. Ní chasann litreacha atá roghnaithe agat ar ais.',
  htSwapTitle: 'Litreacha a athraíonn',
  htSwapBody:
    'Uaireanta, idir babhtaí, cuirtear litir eile in ionad litreach. Feicfidh tú cé acu a d’imigh agus cé acu a tháinig.',
  htLevelsTitle: 'Na leibhéil',
  htLevelEasy:
    'An dá litir dhéag chéanna ar feadh an chluiche, mar sin is féidir iad a fhoghlaim agus liosta focal a iompar i do cheann. Iompaíonn na tíleanna go mall, agus fanann an clár ar fad le feiceáil fada go leor chun do rogha a chríochnú.',
  htLevelMedium:
    'Athraíonn litir anois is arís, mar sin is deacra cuimhneamh ar fhocail a cuireadh i leataobh do níos déanaí. Níos lú ama le breathnú, agus níos lú le machnamh.',
  htLevelHard:
    'Ní chomhairtear focail trí litir a thuilleadh, agus athraíonn litir gach dara babhta nach mór. Is ar éigin a bhíonn an clár le feiceáil sula suaitear é.',
  htLevelInsane:
    'Gach rud le chéile, ar luas. Suaitear an clár beagnach díreach tar éis an bhirt dheireanaigh.',
  htTouchTitle: 'An scáileán tadhaill',
  htTouchBody:
    'Tapáil tíl atá le feiceáil chun a litir a thógáil. Tapáil litir a thóg tú chun í a thabhairt ar ais. Tá Críochnaigh agus Glan faoin gclár.',

  accountTitle: 'Do chuntas',
  menuProfile: 'Mo phróifíl',
  menuGames: 'Mo chluichí',
  menuPublicPage: 'Mo leathanach poiblí',
  menuSignedInAs: 'Sínithe isteach mar',
  signIn: 'Sínigh isteach',
  signOut: 'Sínigh amach',
  signInTitle: 'Sínigh isteach nó cláraigh',
  signInLead: 'Coinníonn cuntas do chluichí, ar gach gléas.',
  signInKeepGame: 'Sínigh isteach chun an cluiche seo a choinneáil, agus gach ceann ina dhiaidh.',
  continueWithApple: 'Lean ar aghaidh le Apple',
  continueWithGoogle: 'Lean ar aghaidh le Google',
  signInOr: 'nó',
  emailLabel: 'Ríomhphost',
  codeLabel: 'Cód',
  emailMeACode: 'Seol cód chugam',
  working: 'Nóiméad…',
  sendNewCode: 'Seol cód nua',
  codeSent: 'Féach ar do ríomhphost. Maireann an cód 10 nóiméad.',
  notNow: 'Ní anois',
  badEmail: 'Ní cosúil gur seoladh ríomhphoist é sin.',
  badCode:
    'Níor oibrigh an cód sin. Rachaidh cóid as feidhm tar éis 10 nóiméad agus oibríonn siad uair amháin.',
  serverBusy:
    'Níorbh fhéidir teagmháil a dhéanamh leis an bhfreastalaí. Bain triail eile as ar ball.',
  serverDown: 'Níorbh fhéidir teagmháil a dhéanamh leis an bhfreastalaí.',
  offline: 'As líne',
  leaderboardTitle: 'Clár na nGaiscíoch',
  leaderboardEmpty: 'Níl aon chluiche ar an gclár seo go fóill. Bí ar an gcéad duine.',
  leaderboardWouldBe: 'Cén áit a mbeadh an cluiche seo',
  leaderboardThisGame: 'An cluiche seo',
  leaderboardRanksHere: 'Cén áit a bhfuil an cluiche seo',
  leaderboardOutside: 'Taobh amuigh de na cúig cinn is fearr.',
  tutAccountTitle: 'Coinnigh do scóir',
  tutAccountBody:
    'Sábhálann cuntas do chluichí, cuireann sé do scóir ar an gclár, agus coinníonn sé do phróifíl.',
  ssoCancelled: 'Cuireadh an síniú isteach ar ceal.',
  ssoExpired: 'Thóg sé sin rófhada. Sínigh isteach arís.',
  ssoNoUsername: 'Níorbh fhéidir an cuntas a chríochnú. Bain triail eile as ar ball.',
  ssoFailed: 'Níor oibrigh sé. Bain triail eile as, nó úsáid do ríomhphost.',
  tabProfile: 'Próifíl',
  countryAny: "B'fhearr liom gan a rá",
  findCountry: 'Cuardaigh tíortha',
  usernameLabel: 'Ainm úsáideora',
  bioLabel: 'Fúm',
  countryLabel: 'Tír',
  bioHint: 'Gan naisc. {left}/{max}',
  save: 'Sábháil',
  saving: 'Á shábháil…',
  saved: 'Sábháilte.',
  nameTaken: 'Tá an t-ainm sin ag duine éigin cheana.',
  nameTooShort: 'Beagán níos faide, le do thoil.',
  nameTooLong: 'Tá sé sin rófhada.',
  nameBadCharacters: 'Litreacha, digití, fleiscíní agus fostríoca amháin.',
  nameBadEdges: 'Caithfidh sé tosú agus críochnú le litir nó digit.',
  nameMixedScripts: 'Roghnaigh aibítir amháin agus fan léi.',
  nameReserved: 'Tá cuma ainm a thugaimid féin amach air sin. Roghnaigh ceann eile.',
  nameUnusable: 'Ní féidir an t-ainm sin a úsáid.',
  bioTooLong: 'Tá spás do {max} carachtar ar a mhéad.',
  bioHasLink: 'Ní cheadaítear naisc anseo.',
  bioHasControl: 'Tá carachtair ann nach féidir a bheith anseo.',
  bioUnusable: 'Ní féidir an téacs sin a úsáid.',
  gamesHeading: 'Cluichí',
  gamesEmpty:
    'Níl aon rud anseo fós. Taispeánfar anseo na cluichí a imríonn tú agus tú sínithe isteach.',
  gamesEmptyShort: 'Níl aon rud anseo fós.',
  gamesLoading: 'Do chluichí á léamh…',
  theirGamesLoading: 'A gcuid cluichí á léamh…',
  profileLoading: 'An phróifíl á léamh…',
  playerNotFound: 'Níl aon duine anseo leis an ainm sin.',
  columnWhen: 'Cathain',
  columnRounds: 'babhta',
  columnGame: 'Cluiche',
  gameLoading: 'An cluiche á léamh…',
  gameUnreadable: 'Níorbh fhéidir an cluiche seo a léamh.',
  gameNotRanked: 'Imríodh le rialacha athraithe, mar sin níl sé rangaithe.',
  detailNotKept: 'Ní choinnítear focail ná cláir an chluiche seo a thuilleadh.',
  openingBoard: 'An clár tosaigh',
  roundByRound: 'Babhta ar bhabhta',
  roundNothing: 'níor aimsíodh faic',
  changeReplaced: '{letter} curtha in ionad i sliotán {slot}',
  changeWildOn: '{letter} saorchárta i sliotán {slot}',
  changeWildOff: '{letter} saorchárta imithe ó shliotán {slot}',
  keepThisGame: 'Coinnigh an cluiche seo',
  savedToYourGames: 'Sábháilte i do chluichí.',

  reportThis: 'Tuairiscigh',
  reportHeading: 'Tuairiscigh é seo',
  reportTheName: 'An t-ainm',
  reportTheBio: 'An cur síos',
  reportTheScore: 'An scór',
  reportWhy: 'Cad atá mícheart leis? Roghnach.',
  reportSend: 'Seol an tuairisc',
  reportSent: 'Go raibh maith agat. Breathnóidh duine air.',
  reportSignIn: 'Sínigh isteach chun rud a thuairisciú.',
  reportGone: 'Níl sé sin ann a thuilleadh.',
  reportCancel: 'Cealaigh',

  deleteAccount: 'Scrios mo chuntas',
  deleteAccountWhat:
    'Baineann sé seo do phróifíl agus gach cluiche a choinnigh tú, go buan. Ní féidir é a chur ar ceal.',
  deleteAccountAsk: 'Seol cód chugam',
  deleteConfirm: 'Scrios go buan',
  deleteGone: 'Tá do chuntas imithe. Go raibh maith agat as imirt.',
  deleteNoAddress:
    'Níl seoladh deimhnithe ar an gcuntas seo, mar sin ní féidir scriosadh a sheiceáil. Déan teagmháil linn agus déanfaimid de láimh é.',

  rulesetFacts: 'Focail le {letters} · tíl nua gach {seconds}',
  plurals: {
    words: {
      one: '{n} fhocal',
      two: '{n} fhocal',
      few: '{n} fhocal',
      many: '{n} bhfocal',
      other: '{n} focal',
    },
    rounds: {
      one: '{n} bhabhta',
      two: '{n} bhabhta',
      few: '{n} bhabhta',
      many: '{n} mbabhta',
      other: '{n} babhta',
    },
    flips: {
      one: '{n} bheart',
      two: '{n} bheart',
      few: '{n} bheart',
      many: '{n} mbeart',
      other: '{n} beart',
    },
    ticks: {
      one: '{n} thic',
      two: '{n} thic',
      few: '{n} thic',
      many: '{n} dtic',
      other: '{n} tic',
    },
    points: {
      one: '{n} phointe',
      two: '{n} phointe',
      few: '{n} phointe',
      many: '{n} bpointe',
      other: '{n} pointe',
    },
    minLetters: {
      one: '{n} litir ar a laghad',
      two: '{n} litir ar a laghad',
      few: '{n} litir ar a laghad',
      many: '{n} litir ar a laghad',
      other: '{n} litir ar a laghad',
    },
    seconds: {
      one: '{n} soicind',
      two: '{n} shoicind',
      few: '{n} shoicind',
      many: '{n} soicind',
      other: '{n} soicind',
    },
  },
}
